import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import {
  Car,
  ArrowLeft,
  CheckCircle2,
  XCircle,
  ChevronRight,
  RotateCcw,
  Trophy,
  Loader2,
  Home,
  FileText,
  CreditCard,
  User,
  LogOut,
  Menu,
  X,
  Lock,
  Bike,
  Truck,
  Bus,
  Tractor,
  RefreshCw
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Icons for license categories
const categoryIcons = {
  A: Bike,
  B: Car,
  C: Truck,
  D: Bus,
  E: Truck,
  Tr: Tractor,
  R: RefreshCw
};

// Colors for license categories
const categoryColors = {
  A: { bg: "from-orange-500 to-red-500", light: "bg-orange-100 text-orange-700" },
  B: { bg: "from-blue-500 to-indigo-600", light: "bg-blue-100 text-blue-700" },
  C: { bg: "from-green-500 to-emerald-600", light: "bg-green-100 text-green-700" },
  D: { bg: "from-purple-500 to-violet-600", light: "bg-purple-100 text-purple-700" },
  E: { bg: "from-cyan-500 to-teal-600", light: "bg-cyan-100 text-cyan-700" },
  Tr: { bg: "from-amber-500 to-yellow-600", light: "bg-amber-100 text-amber-700" },
  R: { bg: "from-slate-500 to-gray-600", light: "bg-slate-100 text-slate-700" }
};

export default function TestsPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [view, setView] = useState("categories"); // categories, quizzes, test, results
  const [licenseCategories, setLicenseCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [quizzes, setQuizzes] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [testResult, setTestResult] = useState(null);
  const [currentQuizNumber, setCurrentQuizNumber] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    fetchLicenseCategories();
  }, []);

  const fetchLicenseCategories = async () => {
    try {
      const response = await axios.get(`${API}/tests/license-categories`);
      setLicenseCategories(response.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const selectLicenseCategory = async (category) => {
    setSelectedCategory(category);
    setLoading(true);
    try {
      const response = await axios.get(`${API}/tests/quizzes/${category.code}`);
      setQuizzes(response.data.quizzes);
      setView("quizzes");
    } catch (error) {
      toast.error("Eroare la încărcarea chestionarelor");
    } finally {
      setLoading(false);
    }
  };

  const startQuiz = async (quizNumber) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/tests/quiz/${selectedCategory.code}/${quizNumber}`);
      setQuestions(response.data.questions);
      setCurrentQuizNumber(quizNumber);
      setAnswers({});
      setCurrentQuestion(0);
      setTestResult(null);
      setView("test");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la încărcarea chestionarului");
    } finally {
      setLoading(false);
    }
  };

  const selectAnswer = (questionId, optionId) => {
    setAnswers({ ...answers, [questionId]: optionId });
  };

  const submitTest = async () => {
    if (Object.keys(answers).length < questions.length) {
      toast.warning("Răspunde la toate întrebările");
      return;
    }

    setLoading(true);
    try {
      const url = `${API}/tests/submit?quiz_number=${currentQuizNumber}&license_category=${selectedCategory.code}`;
      const response = await axios.post(url, { answers });
      setTestResult(response.data);
      setView("results");
    } catch (error) {
      toast.error("Eroare la trimitere");
    } finally {
      setLoading(false);
    }
  };

  const goBackToCategories = () => {
    setView("categories");
    setSelectedCategory(null);
    setQuizzes([]);
  };

  const goBackToQuizzes = () => {
    setView("quizzes");
    setQuestions([]);
    setCurrentQuizNumber(null);
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const sidebarLinks = [
    { name: "Dashboard", path: "/dashboard", icon: Home },
    { name: "Teste", path: "/teste", icon: FileText },
    { name: "Abonamente", path: "/abonamente", icon: CreditCard },
  ];

  const question = questions[currentQuestion];
  const progress = questions.length > 0 ? ((currentQuestion + 1) / questions.length) * 100 : 0;

  return (
    <div className="min-h-screen bg-slate-100" data-testid="tests-page">
      {/* Mobile Header */}
      <div className="lg:hidden bg-white border-b sticky top-0 z-40">
        <div className="flex items-center justify-between p-4">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <Car className="w-6 h-6 text-white" />
            </div>
            <span className="font-bold text-lg font-['Archivo']">AUTO CORA</span>
          </Link>
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2">
            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside className={`
          fixed lg:sticky top-0 left-0 h-screen w-64 bg-slate-900 text-white z-50
          transform transition-transform duration-300 lg:transform-none
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}>
          <div className="p-6 border-b border-slate-700">
            <Link to="/" className="flex items-center gap-2">
              <div className="relative">
                <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                  <Car className="w-6 h-6 text-white" />
                </div>
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full" />
              </div>
              <span className="font-bold text-lg font-['Archivo']">AUTO CORA</span>
            </Link>
          </div>

          <nav className="p-4 space-y-2">
            {sidebarLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  window.location.pathname === link.path 
                    ? 'bg-blue-600 text-white' 
                    : 'text-slate-300 hover:bg-slate-800'
                }`}
              >
                <link.icon className="w-5 h-5" />
                <span>{link.name}</span>
              </Link>
            ))}
          </nav>

          <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-700">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-slate-700 rounded-full flex items-center justify-center">
                <User className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{user?.name}</p>
                <p className="text-sm text-slate-400 truncate">{user?.email}</p>
              </div>
            </div>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="w-full border-slate-600 text-slate-300 hover:bg-slate-800 bg-transparent"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Deconectare
            </Button>
          </div>
        </aside>

        {sidebarOpen && (
          <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
        )}

        {/* Main Content */}
        <main className="flex-1 p-4 lg:p-8 min-h-screen">
          <div className="max-w-6xl mx-auto">
            <AnimatePresence mode="wait">
              
              {/* Categories View */}
              {view === "categories" && (
                <motion.div
                  key="categories"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="space-y-8"
                >
                  <div className="text-center">
                    <h1 className="text-3xl lg:text-4xl font-bold text-slate-900 font-['Archivo'] mb-2">
                      Chestionare Auto DRPCIV
                    </h1>
                    <p className="text-slate-600 text-lg">
                      Alege categoria pentru care vrei să înveți
                    </p>
                  </div>

                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {licenseCategories.map((cat, index) => {
                      const Icon = categoryIcons[cat.code] || Car;
                      const colors = categoryColors[cat.code] || categoryColors.B;
                      
                      return (
                        <motion.div
                          key={cat.code}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.05 }}
                        >
                          <Card 
                            className="group cursor-pointer overflow-hidden border-0 shadow-lg hover:shadow-xl transition-all duration-300 h-full"
                            onClick={() => selectLicenseCategory(cat)}
                            data-testid={`category-${cat.code}`}
                          >
                            <div className={`h-32 bg-gradient-to-br ${colors.bg} flex items-center justify-center relative overflow-hidden`}>
                              <div className="absolute inset-0 bg-black/10 group-hover:bg-black/0 transition-colors" />
                              <Icon className="w-16 h-16 text-white/90 group-hover:scale-110 transition-transform duration-300" />
                              <div className="absolute top-3 right-3">
                                <Badge className="bg-white/20 text-white border-0 text-lg font-bold px-3">
                                  {cat.code}
                                </Badge>
                              </div>
                            </div>
                            <CardContent className="p-5">
                              <h3 className="font-bold text-lg text-slate-900 mb-1">
                                {cat.name}
                              </h3>
                              <p className="text-sm text-slate-500 mb-3">
                                {cat.subcategories}
                              </p>
                              <p className="text-sm text-slate-600 line-clamp-2">
                                {cat.description}
                              </p>
                              <Button 
                                className={`w-full mt-4 bg-gradient-to-r ${colors.bg} hover:opacity-90 text-white border-0`}
                              >
                                Intră și învață
                                <ChevronRight className="w-4 h-4 ml-2" />
                              </Button>
                            </CardContent>
                          </Card>
                        </motion.div>
                      );
                    })}
                  </div>
                </motion.div>
              )}

              {/* Quizzes View */}
              {view === "quizzes" && selectedCategory && (
                <motion.div
                  key="quizzes"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="space-y-6"
                >
                  <div className="flex items-center gap-4 mb-6">
                    <button 
                      onClick={goBackToCategories}
                      className="flex items-center gap-2 text-slate-600 hover:text-slate-900"
                    >
                      <ArrowLeft className="w-5 h-5" />
                      <span>Înapoi la categorii</span>
                    </button>
                  </div>

                  <div className="text-center mb-8">
                    <Badge className={`${categoryColors[selectedCategory.code]?.light} text-lg px-4 py-1 mb-3`}>
                      {selectedCategory.code}
                    </Badge>
                    <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 font-['Archivo']">
                      {selectedCategory.name}
                    </h1>
                    <p className="text-slate-600 mt-1">
                      {selectedCategory.description}
                    </p>
                  </div>

                  <Card className="border-0 shadow-sm">
                    <CardHeader>
                      <CardTitle className="font-['Archivo']">
                        Alege Chestionarul (1-30)
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-5 sm:grid-cols-6 md:grid-cols-10 gap-2">
                        {quizzes.map((quiz) => {
                          const isAvailable = quiz.is_available;
                          const isCompleted = quiz.is_completed;
                          const isPassed = quiz.best_score >= 80;
                          
                          return (
                            <button
                              key={quiz.number}
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                if (isAvailable) startQuiz(quiz.number);
                              }}
                              disabled={!isAvailable}
                              className={`
                                aspect-square rounded-lg font-bold text-lg transition-all relative
                                ${!isAvailable 
                                  ? 'bg-slate-100 text-slate-400 cursor-not-allowed' 
                                  : isCompleted
                                    ? isPassed
                                      ? 'bg-green-500 text-white hover:bg-green-600'
                                      : 'bg-yellow-500 text-white hover:bg-yellow-600'
                                    : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                                }
                              `}
                              data-testid={`quiz-${quiz.number}`}
                            >
                              {quiz.number}
                              {!isAvailable && (
                                <Lock className="w-3 h-3 absolute top-1 right-1 text-slate-400" />
                              )}
                              {isCompleted && isPassed && (
                                <CheckCircle2 className="w-3 h-3 absolute top-1 right-1" />
                              )}
                            </button>
                          );
                        })}
                      </div>
                      <div className="flex flex-wrap gap-4 mt-4 text-sm">
                        <span className="flex items-center gap-2"><div className="w-4 h-4 bg-green-500 rounded" /> Promovat</span>
                        <span className="flex items-center gap-2"><div className="w-4 h-4 bg-yellow-500 rounded" /> Nepromovat</span>
                        <span className="flex items-center gap-2"><div className="w-4 h-4 bg-blue-100 rounded" /> Disponibil</span>
                        <span className="flex items-center gap-2"><div className="w-4 h-4 bg-slate-100 rounded" /> Indisponibil</span>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              )}

              {/* Test View */}
              {view === "test" && question && (
                <motion.div
                  key="test"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <div className="mb-6">
                    <button 
                      onClick={goBackToQuizzes}
                      className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      Înapoi la chestionare
                    </button>
                    
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-600">
                        Întrebarea {currentQuestion + 1} din {questions.length}
                      </span>
                      <div className="flex gap-2">
                        <Badge className={categoryColors[selectedCategory?.code]?.light}>
                          {selectedCategory?.code}
                        </Badge>
                        <Badge variant="outline">Chestionar {currentQuizNumber}</Badge>
                      </div>
                    </div>
                    <Progress value={progress} className="h-2" />
                  </div>

                  <Card className="border-0 shadow-lg">
                    <CardContent className="p-6 lg:p-8">
                      {question.image_url && (
                        <div className="mb-6 flex justify-center">
                          <img 
                            src={question.image_url} 
                            alt="Imagine întrebare"
                            className="max-h-64 rounded-lg shadow-md"
                            onError={(e) => e.target.style.display = 'none'}
                          />
                        </div>
                      )}

                      <h2 className="text-xl font-semibold text-slate-900 mb-6">
                        {question.text}
                      </h2>

                      <RadioGroup
                        value={answers[question.id] || ""}
                        onValueChange={(value) => selectAnswer(question.id, value)}
                        className="space-y-3"
                      >
                        {question.options.map((option) => (
                          <div
                            key={option.id}
                            className={`flex items-center space-x-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                              answers[question.id] === option.id
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-slate-200 hover:border-slate-300 bg-white'
                            }`}
                            onClick={() => selectAnswer(question.id, option.id)}
                          >
                            <RadioGroupItem value={option.id} id={`opt-${option.id}`} />
                            <Label htmlFor={`opt-${option.id}`} className="flex-1 cursor-pointer font-normal">
                              <span className="font-bold mr-2">{option.id.toUpperCase()}.</span>
                              {option.text}
                            </Label>
                          </div>
                        ))}
                      </RadioGroup>

                      <div className="flex justify-between mt-8">
                        <Button
                          variant="outline"
                          onClick={() => setCurrentQuestion(Math.max(0, currentQuestion - 1))}
                          disabled={currentQuestion === 0}
                        >
                          Înapoi
                        </Button>

                        {currentQuestion < questions.length - 1 ? (
                          <Button
                            onClick={() => setCurrentQuestion(currentQuestion + 1)}
                            disabled={!answers[question.id]}
                            className="bg-blue-600 hover:bg-blue-700"
                          >
                            Următoarea
                            <ChevronRight className="w-4 h-4 ml-2" />
                          </Button>
                        ) : (
                          <Button
                            onClick={submitTest}
                            disabled={loading || Object.keys(answers).length < questions.length}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Finalizează"}
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Question Navigation */}
                  <div className="mt-6 flex flex-wrap gap-2 justify-center">
                    {questions.map((q, index) => (
                      <button
                        key={q.id}
                        onClick={() => setCurrentQuestion(index)}
                        className={`w-10 h-10 rounded-lg font-medium transition-colors ${
                          index === currentQuestion
                            ? 'bg-blue-600 text-white'
                            : answers[q.id]
                            ? 'bg-green-100 text-green-700'
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                      >
                        {index + 1}
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Results View */}
              {view === "results" && testResult && (
                <motion.div
                  key="results"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <Card className="border-0 shadow-lg text-center">
                    <CardContent className="p-8">
                      <div className={`w-24 h-24 mx-auto rounded-full flex items-center justify-center mb-6 ${
                        testResult.passed ? 'bg-green-100' : 'bg-red-100'
                      }`}>
                        {testResult.passed ? (
                          <Trophy className="w-12 h-12 text-green-600" />
                        ) : (
                          <XCircle className="w-12 h-12 text-red-600" />
                        )}
                      </div>

                      <h2 className="text-3xl font-bold text-slate-900 mb-2 font-['Archivo']">
                        {testResult.passed ? 'ADMIS!' : 'RESPINS'}
                      </h2>

                      <div className="flex justify-center gap-2 mb-4">
                        <Badge className={categoryColors[selectedCategory?.code]?.light}>
                          {selectedCategory?.name}
                        </Badge>
                        <Badge variant="outline">Chestionar {currentQuizNumber}</Badge>
                      </div>

                      <p className="text-6xl font-black text-slate-900 mb-4 font-['Archivo']">
                        {testResult.percentage}%
                      </p>

                      <p className="text-slate-600 mb-2">
                        {testResult.score} răspunsuri corecte din {testResult.total_questions}
                      </p>
                      <p className="text-sm text-slate-500 mb-8">
                        Minim necesar: 22 corecte (80%)
                      </p>

                      <div className="space-y-4 text-left max-h-96 overflow-y-auto">
                        {questions.map((q, index) => {
                          const answerData = testResult.answers[q.id];
                          return (
                            <div 
                              key={q.id}
                              className={`p-4 rounded-lg ${
                                answerData?.is_correct ? 'bg-green-50' : 'bg-red-50'
                              }`}
                            >
                              <div className="flex items-start gap-3">
                                <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                                  answerData?.is_correct ? 'bg-green-500' : 'bg-red-500'
                                }`}>
                                  {answerData?.is_correct ? (
                                    <CheckCircle2 className="w-4 h-4 text-white" />
                                  ) : (
                                    <XCircle className="w-4 h-4 text-white" />
                                  )}
                                </div>
                                <div className="flex-1">
                                  {answerData?.image_url && (
                                    <img src={answerData.image_url} alt="" className="max-h-24 rounded mb-2" onError={(e) => e.target.style.display='none'} />
                                  )}
                                  <p className="font-medium text-slate-900 mb-2">
                                    {index + 1}. {q.text}
                                  </p>
                                  {!answerData?.is_correct && answerData?.explanation && (
                                    <p className="text-sm text-slate-600 bg-white p-2 rounded">
                                      💡 {answerData.explanation}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      <div className="flex gap-4 justify-center mt-8">
                        <Button variant="outline" onClick={goBackToQuizzes}>
                          <ArrowLeft className="w-4 h-4 mr-2" />
                          Chestionare
                        </Button>
                        <Button
                          onClick={() => startQuiz(currentQuizNumber)}
                          className="bg-blue-600 hover:bg-blue-700"
                        >
                          <RotateCcw className="w-4 h-4 mr-2" />
                          Încearcă din nou
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              )}
            </AnimatePresence>

            {loading && (view === "categories" || view === "quizzes") && (
              <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
                <div className="bg-white p-6 rounded-xl shadow-xl flex items-center gap-4">
                  <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                  <span className="font-medium">Se încarcă...</span>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
